import { useTranslation } from 'react-i18next'

import { useNotification, useTimeZone, useUserData } from '../../../../hooks'
import { normalizeHtml } from '../../../../utils/html'
import { AITextGenerationLocalContextType } from '../../../AITextGeneration'
import { createEmailMessage } from '../../Email.api'
import {
  EmailDraftProvider,
  getDraftProviderPrefix,
} from '../../EmailDraftProvider'
import { EmailModalReplyForm } from '../../EmailModalReplyForm'
import { useEmailCreationFormInitialValues } from '../useEmailCreationFormInitialValues'
import { useEmailGetActiveAccountForCreate } from '../useEmailGetActiveAccountForCreate'
import { getReplyInitialValuesByMode } from '../useEmailReply'

export function useEmailReplyModal({
  getMessage,
  onCloseModal,
  onSetModal,
  onSetPerformingAction,
  onSuccess,
}) {
  const { t } = useTranslation()

  const timeZone = useTimeZone()

  const currentUser = useUserData()

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const { activeAccountForCreate } = useEmailGetActiveAccountForCreate()

  const emailInitial = useEmailCreationFormInitialValues({
    account: activeAccountForCreate,
  })

  async function handleSuccess({ data }) {
    handleAddSuccessNotification('Notification.EmailRepliedTitleKey')

    onCloseModal()

    return onSuccess({ data })
  }

  async function handleSubmit(data) {
    return createEmailMessage({ ...data, text: normalizeHtml(data.text) })
  }

  async function handleOpenReplyModal({ item, mode }) {
    onSetPerformingAction?.(true)

    const message = await getMessage(item)
      .catch(() => null)
      .finally(() => onSetPerformingAction?.(false))

    if (!message?.permissions?.can_reply) {
      handleAddErrorNotification('Notification.EmailActionNotAvailableErrorKey')

      return
    }

    const getInitialValues = getReplyInitialValuesByMode(mode)

    onSetModal(
      <EmailDraftProvider prefix={getDraftProviderPrefix(message.uuid)}>
        <EmailModalReplyForm
          aiLocalContext={{
            object_id: String(message.uuid),
            type: AITextGenerationLocalContextType.EmailReply,
          }}
          formBodyActionsProps={{ MakeFormFullScreenButton: null }}
          initialValues={{
            ...emailInitial,
            ...getInitialValues({
              currentUserId: currentUser.id,
              data: message,
              t,
              timeZone,
            }),
          }}
          resizeConfig={{ initialFullScreen: true }}
          onClose={onCloseModal}
          onSubmit={handleSubmit}
          onSuccess={handleSuccess}
        />
      </EmailDraftProvider>
    )
  }

  return { handleOpenReplyModal }
}
